import { Fragment } from 'react';
import { View, Text, Image, Link } from '@react-pdf/renderer';
import { tiptapPdfStyles as dx } from './pdf-styles';
import type { TiptapDoc } from './types';

interface TiptapNode {
    type: string;
    attrs?: Record<string, unknown>;
    content?: TiptapNode[];
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
    text?: string;
}

interface TiptapImageNode extends TiptapNode {
    type: 'image';
}

// Vertical strip a cell gives up to its label: the caption line (9pt font at
// react-pdf's default ~1.2 line height) plus its bottom margin. Kept in sync
// with `imageCaptionLabel` in pdf-styles.
const CAPTION_ROW_HEIGHT = 13;

function ImageRow({ images }: { images: TiptapImageNode[] }) {
    return (
        <View style={{ flexDirection: 'row', gap: 6, marginVertical: 8 }} wrap={false}>
            {images.map((img, idx) => (
                <View key={idx} style={{ flex: 1, maxHeight: 220 }}>
                    <Image
                        src={typeof img.attrs?.src === 'string' ? img.attrs.src : ''}
                        style={{ width: '100%', maxHeight: 220, objectFit: 'contain' }}
                    />
                </View>
            ))}
        </View>
    );
}

interface RenderCtx {
    list?: { kind: 'bullet' } | { kind: 'ordered'; index: number };
    spacingScale?: number;
    accent?: string;
}

const EMPTY_CTX: RenderCtx = {};

const SCALABLE_KEYS = new Set([
    'lineHeight',
    'marginTop',
    'marginBottom',
    'marginLeft',
    'marginRight',
    'marginVertical',
    'marginHorizontal',
    'paddingTop',
    'paddingBottom',
    'paddingLeft',
    'paddingRight'
]);

function scaleStyle<T extends Record<string, unknown>>(style: T, scale = 1): T {
    if (scale === 1 || !style) return style;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(style)) {
        const value = style[key];
        out[key] = typeof value === 'number' && SCALABLE_KEYS.has(key) ? value * scale : value;
    }
    return out as T;
}

interface InlineMark {
    type: string;
    attrs?: Record<string, unknown>;
}

function applyMarks(
    text: string,
    marks: InlineMark[] | undefined,
    key: string | number
): React.ReactElement {
    let node: React.ReactElement = <Text key={key}>{text}</Text>;
    if (!marks || marks.length === 0) return node;

    const sorted = [...marks].sort((a, b) => {
        if (a.type === 'link') return 1;
        if (b.type === 'link') return -1;
        return 0;
    });

    for (const mark of sorted) {
        switch (mark.type) {
            case 'bold':
            case 'strong':
                node = (
                    <Text key={key} style={dx.bold}>
                        {node}
                    </Text>
                );
                break;
            case 'italic':
            case 'em':
                node = (
                    <Text key={key} style={dx.italic}>
                        {node}
                    </Text>
                );
                break;
            case 'strike':
                node = (
                    <Text key={key} style={dx.strike}>
                        {node}
                    </Text>
                );
                break;
            case 'code':
                node = (
                    <Text key={key} style={dx.code}>
                        {node}
                    </Text>
                );
                break;
            case 'underline':
                node = (
                    <Text key={key} style={{ textDecoration: 'underline' }}>
                        {node}
                    </Text>
                );
                break;
            case 'link': {
                const href = typeof mark.attrs?.href === 'string' ? mark.attrs.href : '';
                node = href ? (
                    <Link key={key} src={href} style={dx.link}>
                        {node}
                    </Link>
                ) : (
                    <Text key={key} style={dx.link}>
                        {node}
                    </Text>
                );
                break;
            }
            default:
                break;
        }
    }
    return node;
}

function renderInlineContent(content: TiptapNode[] | undefined): React.ReactNode {
    if (!content || content.length === 0) return null;
    return content.map((node, i) => {
        if (node.type === 'text' && typeof node.text === 'string') {
            return applyMarks(node.text, node.marks, i);
        }
        if (node.type === 'hardBreak') {
            return <Text key={i}>{'\n'}</Text>;
        }
        return null;
    });
}

function renderImageGrid(node: TiptapNode, keyPrefix: string): React.ReactNode {
    const rows = typeof node.attrs?.rows === 'number' ? node.attrs.rows : 1;
    const cols = typeof node.attrs?.cols === 'number' ? node.attrs.cols : 2;
    const rowHeight = typeof node.attrs?.rowHeight === 'number' ? node.attrs.rowHeight : 180;
    const rawWidths = node.attrs?.colWidths;
    const colWidths: number[] =
        Array.isArray(rawWidths) && rawWidths.length === cols
            ? (rawWidths as number[])
            : Array.from({ length: cols }, () => Math.round(100 / cols));

    const cells = node.content ?? [];

    return (
        <View style={{ flexDirection: 'column', marginVertical: 8 }} wrap={false}>
            {Array.from({ length: rows }, (_, r) => (
                <View
                    key={`${keyPrefix}:r${r}`}
                    style={{ flexDirection: 'row', height: rowHeight, gap: 4 }}
                >
                    {Array.from({ length: cols }, (__, c) => {
                        const cellIndex = r * cols + c;
                        const cell = cells[cellIndex];
                        const src = typeof cell?.attrs?.src === 'string' ? cell.attrs.src : null;
                        const caption =
                            typeof cell?.attrs?.caption === 'string'
                                ? cell.attrs.caption.trim()
                                : '';
                        return (
                            <View
                                key={`${keyPrefix}:r${r}c${c}`}
                                style={{
                                    width: `${colWidths[c]}%`,
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: 2,
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {src ? (
                                    // Figure: label above the photo, left-aligned on it.
                                    // Giving the image an exact height and no width makes
                                    // react-pdf derive its width from the intrinsic ratio,
                                    // so this box shrink-wraps the photo and stays centered
                                    // in the cell — same behavior as the on-screen editor.
                                    <View style={{ alignItems: 'flex-start', maxWidth: '100%' }}>
                                        {caption ? (
                                            <Text style={dx.imageCaptionLabel}>{caption}</Text>
                                        ) : null}
                                        <Image
                                            src={src}
                                            style={{
                                                height: caption
                                                    ? rowHeight - CAPTION_ROW_HEIGHT
                                                    : rowHeight,
                                                maxWidth: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </View>
                                ) : null}
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
}

function renderBlockNodes(
    nodes: TiptapNode[] | undefined,
    ctx: RenderCtx = EMPTY_CTX,
    keyPrefix = ''
): React.ReactNode {
    if (!nodes) return null;
    const out: React.ReactNode[] = [];
    let i = 0;
    while (i < nodes.length) {
        const node = nodes[i];
        if (node.type === 'image') {
            const group: TiptapImageNode[] = [];
            while (i < nodes.length && nodes[i].type === 'image' && group.length < 4) {
                group.push(nodes[i] as TiptapImageNode);
                i++;
            }
            out.push(<ImageRow key={`${keyPrefix}:img-row-${i}`} images={group} />);
        } else if (node.type === 'imageGrid') {
            out.push(
                <Fragment key={`${keyPrefix}:${i}`}>
                    {renderImageGrid(node, `${keyPrefix}:${i}`)}
                </Fragment>
            );
            i++;
        } else {
            out.push(
                <Fragment key={`${keyPrefix}:${i}`}>
                    {renderBlock(node, ctx, `${keyPrefix}:${i}`)}
                </Fragment>
            );
            i++;
        }
    }
    return out;
}

function renderBlock(node: TiptapNode, ctx: RenderCtx, keyPrefix: string): React.ReactNode {
    const s = <T extends Record<string, unknown>>(style: T): T =>
        scaleStyle(style, ctx.spacingScale ?? 1);

    switch (node.type) {
        case 'doc':
            return renderBlockNodes(node.content, ctx, keyPrefix);

        case 'paragraph': {
            const inline = renderInlineContent(node.content);
            return <Text style={s(dx.paragraph)}>{inline ?? ' '}</Text>;
        }

        case 'heading': {
            const level = Number(node.attrs?.level ?? 1);
            const style = level === 1 ? dx.heading1 : level === 2 ? dx.heading2 : dx.heading3;
            return (
                <Text
                    style={level === 3 && ctx.accent ? [s(style), { color: ctx.accent }] : s(style)}
                >
                    {renderInlineContent(node.content)}
                </Text>
            );
        }

        case 'bulletList': {
            return (
                <View style={{ marginVertical: 4 * (ctx.spacingScale ?? 1) }}>
                    {(node.content ?? []).map((li, i) => (
                        <View key={`${keyPrefix}:${i}`} style={s(dx.listItem)} wrap={false}>
                            <Text style={s(dx.listBullet)}>•</Text>
                            <View style={s(dx.listBody)}>
                                {renderBlockNodes(
                                    li.content,
                                    { ...ctx, list: { kind: 'bullet' } },
                                    `${keyPrefix}:${i}`
                                )}
                            </View>
                        </View>
                    ))}
                </View>
            );
        }

        case 'orderedList': {
            const start = Number(node.attrs?.start ?? 1);
            return (
                <View style={{ marginVertical: 4 * (ctx.spacingScale ?? 1) }}>
                    {(node.content ?? []).map((li, i) => (
                        <View key={`${keyPrefix}:${i}`} style={s(dx.listItem)} wrap={false}>
                            <Text style={s(dx.listBullet)}>{`${start + i}.`}</Text>
                            <View style={s(dx.listBody)}>
                                {renderBlockNodes(
                                    li.content,
                                    { ...ctx, list: { kind: 'ordered', index: start + i } },
                                    `${keyPrefix}:${i}`
                                )}
                            </View>
                        </View>
                    ))}
                </View>
            );
        }

        case 'listItem':
            return renderBlockNodes(node.content, ctx, keyPrefix);

        case 'blockquote':
            return (
                <View style={s(dx.blockquote)}>
                    {renderBlockNodes(node.content, ctx, keyPrefix)}
                </View>
            );

        case 'horizontalRule':
            return <View style={s(dx.hr)} />;

        case 'hardBreak':
            return <View style={{ height: 4 * (ctx.spacingScale ?? 1) }} />;

        default: {
            if (
                typeof console !== 'undefined' &&
                typeof process !== 'undefined' &&
                process.env?.NODE_ENV !== 'production'
            ) {
                // eslint-disable-next-line no-console
                console.warn(`[tiptap-pdf] Unknown node type: ${node.type}`);
            }
            return null;
        }
    }
}

export function renderTiptap(
    doc: TiptapDoc,
    keyPrefix: string = 'root',
    options: { spacingScale?: number; accent?: string } = {}
): React.ReactNode {
    const node = doc as unknown as TiptapNode;
    if (node.type !== 'doc') return null;
    const hasScale = options.spacingScale && options.spacingScale !== 1;
    const ctx: RenderCtx =
        hasScale || options.accent
            ? {
                  ...(hasScale ? { spacingScale: options.spacingScale } : {}),
                  ...(options.accent ? { accent: options.accent } : {})
              }
            : EMPTY_CTX;
    return renderBlockNodes(node.content, ctx, keyPrefix);
}
